import { useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useAppDispatch } from '@/store/hooks';
import { tokenStorage } from '@/lib/tokens';
import { env } from '@/lib/env';
import { api } from '@/store/api';

export function useNotificationSocket(options?: { enabled?: boolean }): void {
  const enabled = options?.enabled !== false;
  const dispatch = useAppDispatch();
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!enabled) return undefined;
    const token = tokenStorage.getAccess();
    if (!token) return undefined;

    const socket = io(env.VITE_WS_URL, {
      transports: ['polling'],
      auth: { token },
      reconnectionAttempts: 10,
    });

    socket.on('notification.new', () => {
      dispatch(api.util.invalidateTags([{ type: 'Notification', id: 'LIST' }]));
    });

    socket.on('compliance.evaluated', (payload: { associationId?: number }) => {
      if (payload.associationId) {
        dispatch(api.util.invalidateTags([
          { type: 'Association', id: payload.associationId },
          { type: 'Compliance', id: payload.associationId },
          { type: 'Compliance', id: 'DASHBOARD' },
          { type: 'Compliance', id: 'OPERATIONS_SUMMARY' },
          { type: 'Compliance', id: 'OPERATIONS_ITEMS' },
          { type: 'Compliance', id: `OPERATIONS_DETAIL_${payload.associationId}` },
        ]));
      }
    });

    socket.on('compliance.changed', (payload: { associationId?: number }) => {
      if (payload.associationId) {
        dispatch(api.util.invalidateTags([
          { type: 'Association', id: payload.associationId },
          { type: 'Compliance', id: payload.associationId },
          { type: 'Compliance', id: 'DASHBOARD' },
          { type: 'Compliance', id: 'OPERATIONS_SUMMARY' },
          { type: 'Compliance', id: 'OPERATIONS_ITEMS' },
          { type: 'Compliance', id: `OPERATIONS_DETAIL_${payload.associationId}` },
        ]));
      }
    });

    socket.on('approval.new', () => {
      dispatch(api.util.invalidateTags([{ type: 'Approval', id: 'QUEUE' }]));
    });

    socketRef.current = socket;
    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [dispatch, enabled]);
}
